import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { graphql, useFragment } from "react-relay";
import { TeamScheduleAdminItem } from "./TeamScheduleAdminItem/TeamScheduleAdminItem";
import { AHSeparator, AHText, Box, ListItem, useLayoutContext } from "arkh";
import { useSharedValue } from "react-native-reanimated";

import { DayFilter, RoleFilter } from "./filter";
import { useTeamScheduleAdminScreenListData } from "./useTeamScheduleAdminScreenListData";
import { DailyHeader } from "./DailyHeader";

import { TeamScheduleAdminItemAddContainer } from "./TeamScheduleAdminItem/TeamScheduleAdminItemAddContainer";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { RoutineEditParams } from "../RoutineAdmin/RoutineEditMain/RoutineEditNavigation";
import { TeamScheduleAdminList_routines$key } from "./__generated__/TeamScheduleAdminList_routines.graphql";
import { FormattedMessage } from "react-intl";
import { FloatingAddButton } from "../Product/FloatingAddButton";
import { TeamScheduleAdminListFrequencySelector } from "./TeamScheduleAdminListFrequencySelector";
import { TeamScheduleAdminListEmptyState } from "./TeamScheduleAdminListEmptyState";
import { TeamScheduleAdminFilterRow } from "./TeamScheduleAdminFilterRow";
import { FlashList, FlashListRef } from "@shopify/flash-list";
import { TeamScheduleAdminPendingRoutineCreatedContext } from "./TeamScheduleAdminPendingRoutineCreatedProvider";

export function TeamScheduleAdminList(props: {
  routines: TeamScheduleAdminList_routines$key;
  onCreateRoutine: () => Promise<string> | null;
  onOpenTemplates?: ({
    freq,
    selectedDaysFilter,
  }: {
    freq: "daily" | "weekly" | "monthly";
    selectedDaysFilter: DayFilter;
  }) => void;
  isOrg?: {
    workspaceId: string;
  };
  onEditRoutine: (params: RoutineEditParams) => void;
  initialExpanded?: {
    daily: boolean;
    weekly: boolean;
    monthly: boolean;
  };
  roles?: readonly { readonly id: string; readonly name: string }[];
  hasEmployeeRoleFlag?: boolean;
}) {
  const touchableOpacityAnimation = useSharedValue<number | null>(null);

  const [selectedDaysFilter, setSelectedDayFilter] =
    React.useState<DayFilter>("all");

  const [selectedRoleFilter, setSelectedRoleFilter] =
    React.useState<RoleFilter>("all");

  const routines = useFragment(
    graphql`
      fragment TeamScheduleAdminList_routines on Routine @relay(plural: true) {
        id
        ...useTeamScheduleAdminScreenListData_Routine
      }
    `,
    props.routines
  );
  const [mode, setMode] = useState<"daily" | "weekly" | "monthly">("daily");

  const allRoutines = routines ?? [];
  const res = useTeamScheduleAdminScreenListData({
    routines: allRoutines,
    selectedDaysFilter,
    selectedRoleFilter,
  });

  const listRef = useRef<FlashListRef<any>>(null);
  const { pendingRoutineCreated, setPendingRoutineCreated } = useContext(
    TeamScheduleAdminPendingRoutineCreatedContext
  );

  const animatedRoutineId = useSharedValue<string | null>(null);

  const listData = res[mode];
  useEffect(() => {
    if (pendingRoutineCreated) {
      const index = listData.findIndex((r) => {
        if (
          r.itemType === "ROUTINE_ALL_DAY" ||
          r.itemType === "ROUTINE_WEEKLY" ||
          r.itemType === "ROUTINE_TIME_OF_DAY"
        ) {
          return r.routine.id === pendingRoutineCreated;
        }
        return false;
      });
      if (index >= 0) {
        animatedRoutineId.value = pendingRoutineCreated;
        setPendingRoutineCreated(null);
        listRef.current?.scrollToIndex({
          index,
          animated: true,
        });
      }
    }
  }, [
    animatedRoutineId,
    pendingRoutineCreated,
    listData,
    setPendingRoutineCreated,
  ]);

  const onCreateRoutine = useCallback(
    ({
      startTime,
      freq,
    }: {
      freq: "daily" | "weekly" | "monthly";
      startTime: { hour: number; minute: number } | null;
    }) => {
      props.onCreateRoutine()?.then((id) => {
        setPendingRoutineCreated(id);

        props.onEditRoutine({
          id: id,
          createFreq: freq,
          createDailyDaysFilter: selectedDaysFilter,
          createDailyStartTimeHour: startTime?.hour,
          createDailyStartTimeMinute: startTime?.minute,
          createRoleId:
            selectedRoleFilter !== "all" &&
            selectedRoleFilter !== "without-role"
              ? selectedRoleFilter
              : undefined,
          mode: "CREATE",
        });
      });
    },
    [props, selectedDaysFilter, selectedRoleFilter, setPendingRoutineCreated]
  );

  const _onCreateRoutine = useCallback(
    (props: Parameters<typeof onCreateRoutine>[number]) => () => {
      onCreateRoutine(props);
    },
    [onCreateRoutine]
  );

  type Item = (typeof listData)[number];

  const layout = useLayoutContext();

  const insets = useSafeAreaInsets();
  const onPressInTouchable = useCallback(
    (index: number | null) => () => {
      touchableOpacityAnimation.value = index;
    },
    [touchableOpacityAnimation]
  );

  const renderItem = useCallback(
    ({ item }: { item: Item }) => {
      switch (item.itemType) {
        case "ROUTINE_MONTHLY":
        case "ROUTINE_WEEKLY":
        case "ROUTINE_ALL_DAY":
          return (
            <TeamScheduleAdminItem
              isFirst={false}
              isLast={item.isLast}
              routine={item.routine}
              newlyCreatedId={animatedRoutineId}
              itemType={
                item.itemType === "ROUTINE_WEEKLY" ? "WEEKLY" : "MONTHLY"
              }
            />
          );
        case "ROUTINE_TIME_OF_DAY": {
          return (
            <TeamScheduleAdminItemAddContainer
              index={item.index}
              touchableOpacityAnimation={touchableOpacityAnimation}
              onPressOut={onPressInTouchable(null)}
              onPressInBefore={onPressInTouchable(item.index - 1)}
              onPressInAfter={onPressInTouchable(item.index)}
              onPressAddAfter={_onCreateRoutine({
                freq: "daily",
                startTime: item.startTimeAddAfter,
              })}
              onPressAddBefore={_onCreateRoutine({
                freq: "daily",
                startTime: item.startTimeAddBefore,
              })}
              isFirst={item.isFirst}
              isLast={item.isLast}
            >
              <TeamScheduleAdminItem
                isFirst={item.isFirst}
                isLast={item.isLast}
                routine={item.routine}
                newlyCreatedId={animatedRoutineId}
                itemType="timeOfDay"
              />
            </TeamScheduleAdminItemAddContainer>
          );
        }
        default:
          return null;
      }
    },
    [
      _onCreateRoutine,
      animatedRoutineId,
      onPressInTouchable,
      touchableOpacityAnimation,
    ]
  );

  return (
    <>
      <TeamScheduleAdminListFrequencySelector
        mode={mode}
        setMode={setMode}
        dailyTotalNum={res.unfilteredCounts.dailyTotalNum}
        weekly={res.unfilteredCounts.weeklyTotalNum}
        monthly={res.unfilteredCounts.monthlyTotalNum}
      />
      <TeamScheduleAdminFilterRow
        roles={props.roles ?? []}
        hasEmployeeRoleFlag={props.hasEmployeeRoleFlag ?? false}
        mode={mode}
        selectedRoleFilter={selectedRoleFilter}
        setSelectedRoleFilter={setSelectedRoleFilter}
        selectedDaysFilter={selectedDaysFilter}
        setSelectedDayFilter={setSelectedDayFilter}
      />
      {mode === "daily" ? (
        <DailyHeader numberOfFilteredItems={listData.length} />
      ) : (
        <AHSeparator />
      )}
      <FlashList
        getItemType={(item) => item.itemType}
        contentContainerStyle={{
          paddingBottom: layout.space.medium * 2 + insets.bottom,
        }}
        ListEmptyComponent={
          <TeamScheduleAdminListEmptyState
            mode={mode}
            selectedDaysFilter={selectedDaysFilter}
            selectedRoleFilter={selectedRoleFilter}
            onClearFilters={() => {
              setSelectedDayFilter("all");
              setSelectedRoleFilter("all");
            }}
          />
        }
        ListHeaderComponent={
          props.onOpenTemplates ? (
            <>
              <RoutineTemplateCTA
                onPress={() => {
                  props.onOpenTemplates?.({
                    freq: mode,
                    selectedDaysFilter,
                  });
                }}
              />
              {mode !== "daily" ? <AHSeparator /> : null}
            </>
          ) : null
        }
        ref={listRef}
        data={listData}
        renderItem={renderItem}
        keyExtractor={(item) => item.id}
      />
      <FloatingAddButton
        testID="TeamScheduleAdmin_addRoutine"
        onPress={() => {
          onCreateRoutine({
            freq: mode,
            startTime: { hour: 12, minute: 0 },
          });
        }}
      />
    </>
  );
}

function RoutineTemplateCTA(props: { onPress: () => void }) {
  return (
    <Box space="medium" color="surface-primary">
      <ListItem
        isFree
        border="border-primary"
        onPress={() => {
          props.onPress();
        }}
        right={<ListItem.NavArrow />}
      >
        <AHText type="subtitle" color="text-secondary" spaceBottom="xsmall">
          <FormattedMessage
            defaultMessage="Get started with a template"
            description="TeamScheduleAdmin templates library list item subtitle"
          />
        </AHText>
        <AHText type="title-small" color="text-primary">
          <FormattedMessage
            defaultMessage="Routine Templates"
            description="TeamScheduleAdmin template library list item title"
          />
        </AHText>
      </ListItem>
    </Box>
  );
}
